import { DatePicker, Select } from '@byecode/ui'
import type { ScheduledTriggerConfig, ScheduledTriggerRule } from '@lighthouse/shared'
import { ListItem4ByecodeUi } from '@lighthouse/shared'
import { isValid } from 'date-fns'
import React from 'react'
import styled from 'styled-components'

import * as SC from '../../../styles'
import { CheckboxSelect } from '../CheckboxSelect'

interface CommonRuleProps {}

const SCxInlineItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const SCxInlineItemInsertion = styled.div`
    flex-shrink: 0;
`

interface CommonRuleSettingUnitProps {
    scheduleRule: ScheduledTriggerRule
    value: ScheduledTriggerConfig
    onChange: (value: ScheduledTriggerConfig) => void
}

export const CommonRuleSettingUnit: React.FC<CommonRuleSettingUnitProps> = ({ scheduleRule, value, onChange }) => {
    switch (scheduleRule) {
        case 'MINUTE': {
            const options = Array.from({ length: 59 }, (_, i) => {
                const v = `${i + 1}`
                return { label: v, value: v }
            })
            return (
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>时机</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <SCxInlineItem>
                            <SCxInlineItemInsertion>每</SCxInlineItemInsertion>
                            <Select
                                dropdownProps={{
                                    styles: {
                                        selectDropdown: { height: 300 }
                                    }
                                }}
                                value={value.scheduleMinuteRule}
                                options={options}
                                onChange={val => onChange({ ...value, scheduleMinuteRule: val })}
                            />
                            <SCxInlineItemInsertion>分钟</SCxInlineItemInsertion>
                        </SCxInlineItem>
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            )
        }
        case 'HOUR': {
            const options = Array.from({ length: 23 }, (_, i) => {
                const v = `${i + 1}`
                return { label: v, value: v }
            })
            return (
                <>
                    <ListItem4ByecodeUi>
                        <SC.FormItemLabelWrapper>
                            <SC.FormItemLabel required>时机</SC.FormItemLabel>
                        </SC.FormItemLabelWrapper>
                        <SC.FormItemContent>
                            <SCxInlineItem>
                                <SCxInlineItemInsertion>每</SCxInlineItemInsertion>
                                <Select
                                    dropdownProps={{
                                        styles: {
                                            selectDropdown: { height: 300 }
                                        }
                                    }}
                                    value={value.scheduleHourRule}
                                    options={options}
                                    onChange={val => onChange({ ...value, scheduleHourRule: val })}
                                />
                                <SCxInlineItemInsertion>小时</SCxInlineItemInsertion>
                            </SCxInlineItem>
                        </SC.FormItemContent>
                    </ListItem4ByecodeUi>
                </>
            )
        }
        case 'DAY': {
            const dayOptions = Array.from({ length: 31 }, (_, i) => {
                const v = `${i + 1}`
                return { label: v, value: v }
            })
            const timeOptions = Array.from({ length: 24 * 4 }, (_, i) => {
                const hour = Math.floor(i / 4)
                const minute = 15 * (i % 4)
                const v = `${hour < 10 ? `0${hour}` : hour}:${minute < 10 ? `0${minute}` : minute}`
                return { label: v, value: v }
            })
            return (
                <>
                    <ListItem4ByecodeUi>
                        <SC.FormItemLabelWrapper>
                            <SC.FormItemLabel required>时机</SC.FormItemLabel>
                        </SC.FormItemLabelWrapper>
                        <SC.FormItemContent>
                            <SCxInlineItem>
                                <SCxInlineItemInsertion>每</SCxInlineItemInsertion>
                                <Select
                                    style={{ width: 56 }}
                                    dropdownProps={{
                                        styles: {
                                            selectDropdown: { height: 300 }
                                        }
                                    }}
                                    value={value.scheduleDayRule?.day}
                                    options={dayOptions}
                                    onChange={day =>
                                        onChange({
                                            ...value,
                                            scheduleDayRule: { ...(value.scheduleDayRule || { time: '' }), day }
                                        })
                                    }
                                />
                                <SCxInlineItemInsertion>天的</SCxInlineItemInsertion>
                                <Select
                                    style={{ width: 57 }}
                                    dropdownProps={{
                                        styles: {
                                            selectDropdown: { height: 300 }
                                        }
                                    }}
                                    value={value.scheduleDayRule?.time}
                                    options={timeOptions}
                                    onChange={time =>
                                        onChange({
                                            ...value,
                                            scheduleDayRule: { ...(value.scheduleDayRule || { day: '' }), time }
                                        })
                                    }
                                />
                            </SCxInlineItem>
                        </SC.FormItemContent>
                    </ListItem4ByecodeUi>
                </>
            )
        }
        case 'WEEKDAY': {
            const timeOptions = Array.from({ length: 24 * 4 }, (_, i) => {
                const hour = Math.floor(i / 4)
                const minute = 15 * (i % 4)
                const v = `${hour < 10 ? `0${hour}` : hour}:${minute < 10 ? `0${minute}` : minute}`
                return { label: v, value: v }
            })
            return (
                <>
                    <ListItem4ByecodeUi>
                        <SC.FormItemLabelWrapper>
                            <SC.FormItemLabel required>时机</SC.FormItemLabel>
                        </SC.FormItemLabelWrapper>
                        <SC.FormItemContent>
                            <SCxInlineItem>
                                <SCxInlineItemInsertion>周一到周五的</SCxInlineItemInsertion>
                                <Select
                                    dropdownProps={{
                                        styles: {
                                            selectDropdown: { height: 300 }
                                        }
                                    }}
                                    value={value.scheduleWeekdayRule}
                                    options={timeOptions}
                                    onChange={val => onChange({ ...value, scheduleWeekdayRule: val })}
                                />
                            </SCxInlineItem>
                        </SC.FormItemContent>
                    </ListItem4ByecodeUi>
                </>
            )
        }
        case 'WEEK': {
            const weekOptions = Array.from({ length: 52 }, (_, i) => {
                const v = `${i + 1}`
                const l = `第 ${v} 周`
                return { label: l, value: v }
            })
            const dayOptions = [
                {
                    label: '一',
                    value: '0'
                },
                {
                    label: '二',
                    value: '1'
                },
                {
                    label: '三',
                    value: '2'
                },
                {
                    label: '四',
                    value: '3'
                },
                {
                    label: '五',
                    value: '4'
                },
                {
                    label: '六',
                    value: '5'
                },
                {
                    label: '日',
                    value: '6'
                }
            ]
            const timeOptions = Array.from({ length: 24 * 4 }, (_, i) => {
                const hour = Math.floor(i / 4)
                const minute = 15 * (i % 4)
                const v = `${hour < 10 ? `0${hour}` : hour}:${minute < 10 ? `0${minute}` : minute}`
                return { label: v, value: v }
            })
            return (
                <>
                    {/* <ListItem4ByecodeUi>
                        <SC.FormItemLabelWrapper>
                            <SC.FormItemLabel>重复周</SC.FormItemLabel>
                        </SC.FormItemLabelWrapper>
                        <SC.FormItemContent>
                            <Controller
                                name="config.scheduleWeekRule.week"
                                control={control}
                                render={({ field }) => {
                                    return (
                                        <Select
                                            dropdownProps={{
                                                styles: {
                                                    root: { height: 300 }
                                                }
                                            }}
                                            value={field.value}
                                            options={weekOptions}
                                            onChange={field.onChange}
                                        />
                                    )
                                }}
                            />
                        </SC.FormItemContent>
                    </ListItem4ByecodeUi> */}
                    <ListItem4ByecodeUi>
                        <SC.FormItemLabelWrapper>
                            <SC.FormItemLabel required>星期</SC.FormItemLabel>
                        </SC.FormItemLabelWrapper>
                        <SC.FormItemContent>
                            <CheckboxSelect
                                value={value.scheduleWeekRule?.days}
                                options={dayOptions}
                                onChange={days =>
                                    onChange({
                                        ...value,
                                        scheduleWeekRule: { ...(value.scheduleWeekRule || { time: '', week: '' }), days }
                                    })
                                }
                            />
                        </SC.FormItemContent>
                    </ListItem4ByecodeUi>
                    <ListItem4ByecodeUi>
                        <SC.FormItemLabelWrapper>
                            <SC.FormItemLabel>时间</SC.FormItemLabel>
                        </SC.FormItemLabelWrapper>
                        <SC.FormItemContent>
                            <Select
                                dropdownProps={{
                                    styles: {
                                        selectDropdown: { height: 300 }
                                    }
                                }}
                                value={value.scheduleWeekRule?.time}
                                options={timeOptions}
                                onChange={time =>
                                    onChange({
                                        ...value,
                                        scheduleWeekRule: { ...(value.scheduleWeekRule || { days: [], week: '' }), time }
                                    })
                                }
                            />
                        </SC.FormItemContent>
                    </ListItem4ByecodeUi>
                </>
            )
        }
        case 'MONTH': {
            const monthOptions = Array.from({ length: 12 }, (_, i) => {
                const v = `${i + 1}`
                const l = `第 ${v} 个月`
                return { label: l, value: v }
            })
            const dayOptions = Array.from({ length: 31 }, (_, i) => {
                const v = `${i + 1}`
                const l = `第 ${v} 天`
                return { label: l, value: v }
            })
            const timeOptions = Array.from({ length: 24 * 4 }, (_, i) => {
                const hour = Math.floor(i / 4)
                const minute = 15 * (i % 4)
                const v = `${hour < 10 ? `0${hour}` : hour}:${minute < 10 ? `0${minute}` : minute}`
                return { label: v, value: v }
            })
            return (
                <>
                    {/* <ListItem4ByecodeUi>
                        <SC.FormItemLabelWrapper>
                            <SC.FormItemLabel>重复月</SC.FormItemLabel>
                        </SC.FormItemLabelWrapper>
                        <SC.FormItemContent>
                            <Controller
                                name="config.scheduleMonthRule.month"
                                control={control}
                                render={({ field }) => {
                                    return (
                                        <Select
                                            dropdownProps={{
                                                styles: {
                                                    root: { height: 300 }
                                                }
                                            }}
                                            value={field.value}
                                            options={monthOptions}
                                            onChange={field.onChange}
                                        />
                                    )
                                }}
                            />
                        </SC.FormItemContent>
                    </ListItem4ByecodeUi> */}
                    <ListItem4ByecodeUi>
                        <SC.FormItemLabelWrapper>
                            <SC.FormItemLabel required>天</SC.FormItemLabel>
                        </SC.FormItemLabelWrapper>
                        <SC.FormItemContent>
                            <CheckboxSelect
                                value={value.scheduleMonthRule?.days}
                                options={dayOptions}
                                onChange={days =>
                                    onChange({
                                        ...value,
                                        scheduleMonthRule: { ...(value.scheduleMonthRule || { time: '', month: '' }), days }
                                    })
                                }
                            />
                        </SC.FormItemContent>
                    </ListItem4ByecodeUi>
                    <ListItem4ByecodeUi>
                        <SC.FormItemLabelWrapper>
                            <SC.FormItemLabel>时间</SC.FormItemLabel>
                        </SC.FormItemLabelWrapper>
                        <SC.FormItemContent>
                            <Select
                                dropdownProps={{
                                    styles: {
                                        selectDropdown: { height: 300 }
                                    }
                                }}
                                value={value.scheduleMonthRule?.time}
                                options={timeOptions}
                                onChange={time =>
                                    onChange({
                                        ...value,
                                        scheduleMonthRule: { ...(value.scheduleMonthRule || { days: [], month: '' }), time }
                                    })
                                }
                            />
                        </SC.FormItemContent>
                    </ListItem4ByecodeUi>
                </>
            )
        }

        default: {
            return (
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>时机</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        {(() => {
                            const raw = (value as any).onceTime
                            const dateValue = raw && isValid(new Date(raw)) ? new Date(raw) : undefined
                            return (
                                <DatePicker
                                    clearable
                                    value={dateValue}
                                    showTime
                                    onChange={val => onChange({ ...value, onceTime: val })}
                                    onClear={() => onChange({ ...value, onceTime: '' as any })}
                                />
                            )
                        })()}
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            )
        }
    }
}
